import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

export type UserRole = "admin" | "user" | "secretary";

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
}

export interface AssignedUser {
  id: number;
  email: string;
  name: string;
  role: string;
  assigned_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /** For secretary: the user they are currently acting on behalf of */
  actingAs: AssignedUser | null;
  setActingAs: (user: AssignedUser | null) => void;
  /** For secretary: list of users they can act as */
  assignedUsers: AssignedUser[];
  loadAssignedUsers: () => Promise<void>;
}

const SESSION_KEY = "cardbuzz_acting_as";

function loadPersistedActingAs(): AssignedUser | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  refresh: async () => {},
  actingAs: null,
  setActingAs: () => {},
  assignedUsers: [],
  loadAssignedUsers: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [actingAs, _setActingAs] = useState<AssignedUser | null>(loadPersistedActingAs);
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);

  // Wrap setActingAs to also persist to sessionStorage
  const setActingAs = useCallback((u: AssignedUser | null) => {
    _setActingAs(u);
    if (u) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(u));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<{ user: User | null }>("/api/v1/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAssignedUsers = useCallback(async () => {
    try {
      const data = await api.get<AssignedUser[]>("/api/v1/users/me/assigned-users");
      setAssignedUsers(data);
      // Auto-select first only if nothing is currently selected
      // Use functional update to avoid stale closure on actingAs
      if (data.length > 0) {
        _setActingAs(prev => {
          if (prev) {
            // Validate persisted selection is still valid
            const stillValid = data.find(u => u.id === prev.id);
            if (stillValid) return prev;
          }
          // Auto-select first assigned user
          const first = data[0];
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(first));
          return first;
        });
      }
    } catch {
      setAssignedUsers([]);
    }
  }, []); // No actingAs dependency — uses functional update instead

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Load assigned users when secretary logs in
  useEffect(() => {
    if (user?.role === "secretary") {
      loadAssignedUsers();
    } else if (user && (user.role === "admin" || user.role === "user")) {
      // Clear acting-as if user is no longer a secretary
      setActingAs(null);
      setAssignedUsers([]);
    }
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ user, loading, refresh, actingAs, setActingAs, assignedUsers, loadAssignedUsers }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
